import { ApiService, handlePriceBelowMinimumError } from "../../helpers";
import { PaginationCollection } from "../models";
import { EventsBrokerPmsApiService } from "./events-broker.service";

/**
 * @class AbstractPmsApiService
 * @abstract
 * @template M
 * @extends {ApiService<M>}
 */
export class AbstractPmsApiService extends ApiService {

    /**
     * @param {$http} http
     * @param {ConfigPmsApiService} config
     * @param {EventsBrokerPmsApiService} eventsBroker
     * @param {$injector} $injector
     */
    constructor(http, config, eventsBroker, $injector) {

        super(http);

        /**
         * @type {ConfigPmsApiService}
         * @protected
         */
        this.config = config;

        /**
         * @type {EventsBrokerPmsApiService}
         */
        this.eventsBroker = eventsBroker;
        
        /**
         * @type {$injector}
         * @protected
         */
        this.$injector = $injector;
        
        /**
         * Flag to prevent infinite token refresh loops
         * @type {boolean}
         * @protected
         */
        this._isRefreshingToken = false;
        
        /**
         * Store failed requests to retry them after token refresh
         * @type {Array}
         * @protected
         */
        this._failedRequests = [];
    }

    /**
     * @type {string[]}
     */
    static get $inject() {

        return [ '$http', 'ConfigPmsApiService', 'EventsBrokerPmsApiService', '$injector' ];
    }

    /**
     * @type {Object.<string, string>}
     * @protected
     */
    get authHeaders() {

        const headers = this.headers;

        headers[ 'Authorization' ] = `Bearer ${this.config.authToken}`;

        return headers;
    }

    /**
     * @type {string}
     * @protected
     */
    get endpoint() {

        return this.config.endpoint;
    }

    /**
     * @param {Object} response
     * @return {Object}
     * @protected
     */
    handle422Error(response) {

        // Guard: some services don't receive $injector; never let the price-limit
        // hook throw, which would replace the real error response with a TypeError.
        if (this.$injector) {

            handlePriceBelowMinimumError(response, {
                $uibModal: this.$injector.get('$uibModal'),
                $translate: this.$injector.get('$translate')
            });
        }

        return super.handle422Error(response);
    }

    handle400Error(response) {

        const errorData = response.data.error;

        if (errorData.type === EventsBrokerPmsApiService.ERROR_TYPE_SUBSCRIPTION) {

            this.eventsBroker.events.emit(errorData.code, response, this.constructor);
        }

        return response;
    }

    /**
     * @param {Object} response
     * @return {Object|Promise}
     * @protected
     */
    handle401Error(response) {
        // If we're already refreshing the token, queue this request
        if (this._isRefreshingToken) {
            return new Promise((resolve, reject) => {
                this._failedRequests.push({
                    resolve,
                    reject,
                    config: response.config
                });
            });
        }

        this._isRefreshingToken = true;
        
        // Get the AuthService using the injected $injector to avoid circular dependencies
        const AuthService = this.$injector.get('AuthService');
        
        // Attempt to refresh the token
        return AuthService.refreshToken()
            .then(token => {
                this._isRefreshingToken = false;
                
                // Retry all failed requests with the new token
                this._retryFailedRequests();
                
                // Retry the current failed request
                response.config.headers['Authorization'] = `Bearer ${this.config.authToken}`;
                return this.http(response.config);
            })
            .catch(error => {
                this._isRefreshingToken = false;
                
                // If token refresh fails, reject all queued requests
                this._rejectFailedRequests(error);
                
                // Then emit unauthorized event
                response.message = response.message || "Unauthenticated";
                this.eventsBroker.events.emit(EventsBrokerPmsApiService.EVENT_UNAUTHENTICATED, response, this.constructor);
                
                return Promise.reject(response);
            });
    }
    
    /**
     * Retry all failed requests with new token
     * @protected
     */
    _retryFailedRequests() {
        this._failedRequests.forEach(request => {
            // Update Authorization header with new token
            request.config.headers['Authorization'] = `Bearer ${this.config.authToken}`;
            
            // Retry the request
            this.http(request.config)
                .then(response => request.resolve(response))
                .catch(error => request.reject(error));
        });
        
        this._failedRequests = [];
    }
    
    /**
     * Reject all failed requests with the given error
     * @param {Object} error 
     * @protected
     */
    _rejectFailedRequests(error) {
        this._failedRequests.forEach(request => {
            request.reject(error);
        });
        
        this._failedRequests = [];
    }
    
    /**
     * @param {Object} response
     * @return {Object}
     * @protected
     */
    handle403Error(response) {
        
        response.message = response.message || "Unauthorized";
        
        this.eventsBroker.events.emit(EventsBrokerPmsApiService.EVENT_UNAUTHORIZED, response, this.constructor);
        
        return response;
    }
    
    /**
     * @param {Object} response
     * @return {Object}
     * @protected
     */
    handle500Error(response) {
        
        response.message = response.message || "Fatal error";
        
        this.eventsBroker.events.emit(EventsBrokerPmsApiService.EVENT_FATAL_ERROR, response, this.constructor);
        
        return response;
    }

    /**
     * @param {Object.<string, *>} paginationData
     * @param {...*} params
     * @returns {PaginationCollection<M>}
     * @protected
     */
    newPaginationCollection(paginationData, ...params) {

        return new PaginationCollection({
                                            perPage: paginationData.per_page,
                                            page: paginationData.current_page,
                                            total: paginationData.total,
                                            from: paginationData.from,
                                            to: paginationData.to,
                                            models: paginationData.data.map(
                                                (attributes) => this.newModel(attributes, ...params))
                                        });
    }

    /**
     * @param {...*} params
     * @return {function(response: Object): PaginationCollection<M>}
     * @protected
     */
    getPaginationCollector(...params) {

        return (response) => {

            return this.newPaginationCollection(this.extractData(response), ...params);
        };
    }

    /**
     * @abstract
     * @returns {Promise.<M[]|Model[]|Object>}
     */
    get () {

        throw new Error("Abstract 'get' method should be implemented in derived class");
    }

    /**
     * @abstract
     * @param {M|Model} model
     * @param {...*=} params
     * @returns {Promise.<M|Model|boolean>}
     */
    create(model) {

        throw new Error("Abstract 'create' method should be implemented in derived class");
    }

    /**
     * @abstract
     * @param {M|Model} model
     * @param {...*=} params
     * @returns {Promise.<M|Model|boolean>}
     */
    update(model) {

        throw new Error("Abstract 'update' method should be implemented in derived class");
    }

    /**
     * @abstract
     * @param {M|Model} model
     * @returns {Promise.<boolean>}
     */
    delete(model) {

        throw new Error("Abstract 'delete' method should be implemented in derived class");
    }

    /**
     * @param {M|Model} model
     * @param {...*=} params
     * @returns {Promise.<M|Model|boolean>}
     */
    save(model, ...params) {

        return model.exists ? this.update(model, ...params) : this.create(model, ...params);
    }
}



// WEBPACK FOOTER //
// ./src/pms-api/services/abstract.service.js